import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import PasswordChecklist from 'react-password-checklist';
import Grid from '@mui/material/Grid';
import LoginRedirect from '../../utils/redirect-login';
// hooks
import { useBoolean } from '../../hooks/use-boolean';
// routes
import { paths } from '../../routes/paths';
import { RouterLink } from '../../routes/components';
import { useRouter } from '../../routes/hooks';
// auth
import Auth from '../../services/auth';
// components
import Iconify from '../../components/iconify';

import FormProvider, { RHFTextField } from '../../components/hook-form';
import { useAuthContext } from '../../auth/hooks';
import googleSignup from './google-signup';
import { signUpCognito } from '../../services/cognitoService';

// ----------------------------------------------------------------------

export default function RegisterView() {
  const router = useRouter();

  const { login } = useAuthContext();

  const [errorMsg, setErrorMsg] = useState('');
  const [isValid, setIsValid] = useState(false);

  const password = useBoolean();

  const RegisterSchema = Yup.object().shape({
    email: Yup.string()
      .required('El correo electrónico es requerido.')
      .email('El correo electrónico debe ser una dirección de correo electrónico válida.'),
    password: Yup.string().required('La contraseña es requerida.'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [capsLockActive, setCapsLockActive] = useState(false);

  // const handleResponse = useCallback(
  //   async (response) => {
  //     googleSignup(response, router, setErrorMsg, login);
  //   },
  //   [router, login]
  // );

  // useEffect(() => {
  //   /* global google */
  //   google.accounts.id.initialize({
  //     client_id: process.env.REACT_APP_GOOGLE_DIGLO_CLIENT_ID,
  //     callback: handleResponse,
  //   });
  //   google.accounts.id.renderButton(document.getElementById('googleSignupButton'), {
  //     theme: 'outline',
  //     size: 'large',
  //     logo_alignment: 'center',
  //   });
  // }, [handleResponse]);

  // const onSubmit = handleSubmit(async (user) => {
  //   // eslint-disable-next-line no-control-regex
  //   const reEmail = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
  //   // check to see if the email match the regex, then see if the user already is in firebase, if not move to validation-stepper
  //   if (user.email.match(reEmail) === false) {
  //     setErrorMsg('Direccion de correo no valida. rx');
  //     return;
  //   }
  //   user.name = user.email;
  //   user.social = false;

  //   try {
  //     const response = await Auth.getUser(user);
  //     if (response.status === 202) {
  //       sessionStorage.setItem('data', JSON.stringify(user));
  //       router.push(paths.clients.validation);
  //     } else {
  //       LoginRedirect({
  //         message: 'El usuario ya esta registrado',
  //         severity: 'warning',
  //         redirect: router,
  //       });
  //     }
  //   } catch (error) {
  //     if (error.request?.status === 409 || error.request?.status === 403) {
  //       setErrorMsg(error.response.data.message);
  //       return;
  //     }
  //     setErrorMsg('Ocurrio un error inesperado, por favor intentelo de nuevo.');
  //   }
  // });

  const onSubmit = handleSubmit(async (user) => {
  try {
    const result = await signUpCognito(user.email, user.password);
    sessionStorage.setItem('data', JSON.stringify(user));
    router.push('/');
  } catch (error) {
    setErrorMsg(error.message);
  }
});

  const renderHead = (
    <>
      <Typography variant="h4">Registrarme</Typography>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}
    </>
  );

  const renderForm = (
    <>
      <RHFTextField name="email" label="Correo electrónico" />
      <RHFTextField
        name="password"
        label="Contraseña"
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onKeyDown={(e) => {
          setCapsLockActive(e.getModifierState('CapsLock'));
        }}
      />
      {capsLockActive && (
        <Alert
          key="warning"
          severity="warning"
          variant="outlined"
          onClose={() => {}}
          sx={{ width: 1 }}
        >
          Tienes activo las mayúsculas.
        </Alert>
      )}
      {methods.watch('password') && (
        <PasswordChecklist
          rules={['minLength', 'specialChar', 'number', 'capital']}
          minLength={6}
          value={methods.watch('password')}
          onChange={(valid) => {
            setIsValid(valid);
          }}
          messages={{
            minLength: 'La contraseña tiene más de 6 caracteres.',
            specialChar: 'La contraseña tiene caracteres especiales.',
            number: 'La contraseña tiene un número.',
            capital: 'La contraseña tiene una letra mayúscula.',
          }}
          style={{ fontSize: '12px' }}
        />
      )}
      <LoadingButton
        fullWidth
        color="primary"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        disabled={!isValid || !methods.watch('password')}
      >
        Crear una cuenta
      </LoadingButton>
    </>
  );

  const renderFooter = (
    <Stack direction="row" spacing={0.5}>
      <Typography variant="body2"> ¿Ya tienes una cuenta? </Typography>

      <Link href={paths.auth.login} component={RouterLink} variant="subtitle2">
        Iniciar sesión
      </Link>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        {renderHead}
        {renderForm}

        <Grid container alignItems="center" spacing={3} sx={{ p: 2 }}>
          <Grid item xs>
            <Divider />
          </Grid>
          <Grid item>O crea tu usuario con:</Grid>
          <Grid item xs>
            <Divider />
          </Grid>
        </Grid>

        <Stack spacing={1} alignItems="center" sx={{ p: 0 }}>
          <div role="presentation" id="googleSignupButton" />
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />
      {renderFooter}
    </FormProvider>
  );
}
